import { isSameDay } from 'date-fns';
import { proxy, useSnapshot } from 'valtio';
import { getManyFromList } from '@soundxyz/utils';
import type { EnumValueType } from '@soundxyz/utils/const';
import { ActivityFeedGroupInfoTypenames } from '@soundxyz/utils/const';
import type {
  ActivityFeedFilterArgs,
  ActivityFeedGroupFilterArgs,
  ActivityFeedHookQuery,
  ActivityFeedType,
} from '@graphql/reactQuery';
import { ActivityFeedGroupFilterOption } from '@graphql/reactQuery';

export const PAGE_SIZE = 5;
export type ActivityGroup = NonNullable<
  NonNullable<ActivityFeedHookQuery['activityFeed']>['activityFeedGroups']['edges'][number]
>['node'];
export type ActivityGroupWithDivider = ActivityGroup & { isSameDay: boolean };

export const FilteredActivityFeedTypes = {
  NotableCollectors: 'NOTABLE_COLLECTORS',
  User: 'USER',
  Global: 'GLOBAL',
} as const satisfies Partial<typeof ActivityFeedType>;
export type FilteredActivityFeedTypes = EnumValueType<typeof FilteredActivityFeedTypes>;

const initialActivityFeedFilterArgs = {
  activityFeedType: FilteredActivityFeedTypes.NotableCollectors,
} satisfies ActivityFeedFilterArgs;

const initialActivityFeedGroupFilterArgs = {
  types: [ActivityFeedGroupFilterOption.Posts],
} satisfies ActivityFeedGroupFilterArgs;

export type ActivityFeedStateType = {
  activityFeedFilter: ActivityFeedFilterArgs;
  activityFeedGroupFilter: {
    types?: ActivityFeedGroupFilterOption[];
  };
  fetchedFilterSettingsFromLocalStorage: boolean;
  scrollIndex: number;
};

export const ActivityFeedState: ActivityFeedStateType = proxy({
  activityFeedFilter: initialActivityFeedFilterArgs,
  activityFeedGroupFilter: initialActivityFeedGroupFilterArgs,
  fetchedFilterSettingsFromLocalStorage: false,
  scrollIndex: 0,
});

export const useActivityFeedState = () => useSnapshot(ActivityFeedState);

export function addDividerDateCheck<T extends { latestActivityOccurenceAt: number }>(
  activityFeedGroups: T[],
): Array<T & { isSameDay: boolean }> {
  return activityFeedGroups.map((activityGroup, i) => ({
    ...activityGroup,
    isSameDay:
      i > 0 &&
      isSameDay(
        activityFeedGroups?.[i - 1]?.latestActivityOccurenceAt || 0,
        activityGroup.latestActivityOccurenceAt,
      ),
  }));
}

export function filterOutRevertedActivity(groups: ActivityGroup[]) {
  return getManyFromList(
    groups,
    group =>
      // Filter out playlist activity updates that have been reverted by a later update and no releases added
      // Eg a user creates a playlist, then deletes it
      !(
        (group.information.__typename ===
          ActivityFeedGroupInfoTypenames.ReleasesAddedToShelfAggregate &&
          (group.information.shelf === null || group.information.releasesAdded.length === 0)) ||
        (group.information.__typename === ActivityFeedGroupInfoTypenames.ShelfCreatedAggregate &&
          group.information.shelf === null) ||
        (group.information.__typename === ActivityFeedGroupInfoTypenames.UserLikedSongsAggregate &&
          (group.information.shelf === null || group.information.releasesLiked.length === 0)) ||
        (group.information.__typename ===
          ActivityFeedGroupInfoTypenames.UserLikedPlaylistAggregate &&
          group.information.shelf === null)
      ) && group,
  );
}

export function filterOutNonExistentType(groups: ActivityGroup[]) {
  return getManyFromList(groups, group => {
    switch (group.information.__typename) {
      case 'SongCollectedByManyAggregate':
      case 'ReleaseDroppedAggregate':
      case 'ReleasesAddedToShelfAggregate':
      case 'ShelfCreatedAggregate':
      case 'UserCollectedManySongsAggregate':
      case 'UserLikedPlaylistAggregate':
      case 'UserCreatedPostAggregate':
      case 'UserLikedSongsAggregate': {
        return group;
      }
      default:
        return null;
    }
  });
}
